// src/components/general/Tabs/PersonaRedSocial/PersonaRedSocialTab.jsx
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ConfirmDeleteDialog from '../../../security/ConfirmDeleteDialog';
import Modal from '../Catalogos/PersonaRedSocial/PersonaRedSocialModalTab';
import { ENDPOINTS } from '../../Utils/Endpoints';
import { Models } from '../../Utils/ModelsGeneral';
import sigmaEdit from '../../../Pages/img/Editar.png';
import sigmaDelete from '../../../Pages/img/Borrar.png';
import AlertDialog from '../../../Lms/Components/Shared/AlertDialog';

/* =========================
   Config local del TAB
   ========================= */
const TITLETAB = 'Redes sociales';
const idKey    = 'idPersonasRedesSociales';
const nameKey  = 'nikName';
const fkKey    = 'idPersona';

const endpoint     = ENDPOINTS.personaRedSocial;
const endpointById = ENDPOINTS.personaRedSocialById;
const normalize    = Models.personaRedSocial;

// 👇 maps usando uRL (como en el normalizer)
const maps = {
  mapCreate: (f) => ({
    idPersona: Number(f.idPersona) || 0,
    idRedSocial: Number(f.idRedSocial) || 0,
    nikName: f.nikName,
    url: f.url,
  }),
  mapUpdate: (id, f) => ({
    idPersonasRedesSociales: id,
    idPersona: Number(f.idPersona) || 0,
    idRedSocial: Number(f.idRedSocial) || 0,
    nikName: f.nikName,
    url: f.url,
  }),
};

export default function PersonaRedSocialTab({ fkValue = 0, readOnly = false }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');

  const [modalOpen, setModalOpen]   = useState(false);
  const [modalMode, setModalMode]   = useState('new'); // 'new' | 'edit'
  const [editData, setEditData]     = useState(null);
  const [editingId, setEditingId]   = useState(null);

  const [deleteOpen, setDeleteOpen]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  // 👉 catálogo de redes sociales para mostrar nombre en vez de ID
  const [redesSociales, setRedesSociales] = useState([]);

  const httpGetList = async () => axios.get(endpoint);
  const httpPost    = (payload) => axios.post(endpoint, payload);
  const httpPutBase = (payload) => axios.put(endpoint, payload);

  const load = async () => {
    setLoading(true);
    try {
      const res = await httpGetList();
      const arr = (res?.data || []).map(normalize);
      setRows(arr);
    } catch (err) {
      console.error(err);
      triggerAlert('Ocurrió un error al cargar las redes sociales. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Cargar catálogo de Cat_RedesSociales (para nombreRedSocial)
  useEffect(() => {
    let canceled = false;

    async function loadCatalogRedes() {
      try {
        const res = await axios.get(ENDPOINTS.redSocial);
        const raw = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        const norm = raw.map((x) => Models.redSocial(x));
        if (!canceled) setRedesSociales(norm);
      } catch (err) {
        console.error('Error al cargar catálogo Cat_RedesSociales', err);
      }
    }

    loadCatalogRedes();
    return () => { canceled = true; };
  }, []);

  // Índice para buscar nombreRedSocial por idRedSocial
  const redesIndex = useMemo(() => {
    const map = {};
    redesSociales.forEach((r) => {
      if (r && r.idRedSocial != null) {
        map[r.idRedSocial] = r;
      }
    });
    return map;
  }, [redesSociales]);

  function formatRedSocial(row) {
    const rs = redesIndex[row.idRedSocial];
    if (!rs) return row.idRedSocial ?? '';
    return rs.nombreRedSocial || `Red social ${row.idRedSocial}`;
  }

  // Filtrar por fk (persona)
  const rowsByPersona = useMemo(() => {
    if (!fkValue) return rows;
    return rows.filter((r) => r[fkKey] === fkValue);
  }, [rows, fkValue]);

  // Filtro por texto
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rowsByPersona;

    return rowsByPersona.filter((r) => {
      const idPersonaText = String(r.idPersona ?? '').toLowerCase();
      const redNombre     = (formatRedSocial(r) || '').toLowerCase();
      const nick          = (r.nikName || '').toLowerCase();
      const url           = (r.url || '').toLowerCase();

      return (
        idPersonaText.includes(q) ||
        redNombre.includes(q) ||
        nick.includes(q) ||
        url.includes(q)
      );
    });
  }, [rowsByPersona, query, redesIndex]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    if (readOnly) return;
    setModalMode('new');
    setEditData(null);
    setEditingId(null);
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    if (readOnly) return;
    setModalMode('edit');
    setEditData(row);
    setEditingId(row[idKey]);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditData(null);
    setEditingId(null);
  };

  const handleSubmitFromModal = async (form, mode) => {
    try {
      setLoading(true);

      // Aseguramos que el fk (idPersona) sea el del tab
      const baseForm = {
        ...form,
        [fkKey]: fkValue || form[fkKey],
      };

      if (mode === 'new') {
        const payload = maps.mapCreate(baseForm);
        await httpPost(payload);
      } else {
        const payload = maps.mapUpdate(editingId, baseForm);
        await httpPutBase(payload);
      }

      await load();
      handleCloseModal();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || 'Ocurrió un error al guardar. Inténtelo más tarde.';
      triggerAlert(msg);
    } finally {
      setLoading(false);
    }
  };

  const onAskDelete = (row) => {
    if (readOnly) return;
    setDeleteTarget({
      id: row[idKey],
      name: row[nameKey] || row.url || 'Red social',
    });
    setDeleteOpen(true);
  };

  const onConfirmDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      await load();
    } catch (err) {
      console.error(err);
      triggerAlert('Ocurrió un error al eliminar. Inténtelo más tarde.');
    } finally {
      setLoading(false);
      setDeleteOpen(false);
      setDeleteTarget({ id: 0, name: '' });
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      {label}
    </button>
  );

  const pages = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex   = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="roles-tab-shell">
      <div className="roles-topbar" style={{ marginBottom: '.4rem' }}>
        <h3 className="roles-title" style={{ marginRight: 'auto' }}>
          {TITLETAB}
        </h3>

        <input
          className="roles-search"
          placeholder="Buscar por red social, nick, URL…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          style={{ maxWidth: 260 }}
        />

        {!readOnly && (
          <button
            className="btn-action"
            type="button"
            onClick={handleNew}
          >
            ＋ Nuevo
          </button>
        )}
      </div>

      <div className="roles-table-wrap">
        <div className="roles-table-scroller">
          <table className="roles-table">
            <thead>
              <tr>
                <th>Red social</th>
                <th>Usuario / Nick</th>
                <th>URL</th>
                {!readOnly && (
                  <>
                    <th style={{ textAlign: 'center' }}>Editar</th>
                    <th style={{ textAlign: 'center' }}>Borrar</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {paged.map((r) => (
                <tr key={r[idKey]}>
                  <td>{formatRedSocial(r)}</td>
                  <td>{r.nikName}</td>
                  <td>{r.url}</td>

                  {!readOnly && (
                    <>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--del"
                          onClick={() => onAskDelete(r)}
                          aria-label="Borrar"
                          title="Borrar"
                        >
                          <img src={sigmaDelete} alt="" className="rk-ico" />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}

              {!loading && paged.length === 0 && (
                <tr>
                  <td colSpan={readOnly ? 3 : 5}>Sin resultados.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginación */}
      <div className="roles-pagination">
        <div className="roles-pagination-left">
          {filtered.length > 0 ? (
            <span>
              Mostrando {startIndex}–{endIndex} de {filtered.length}
            </span>
          ) : (
            <span>0 resultados</span>
          )}
        </div>
        <div className="roles-pagination-right">
          <PageBtn
            label="‹"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          />
          {pages.map((n) => (
            <PageBtn
              key={n}
              label={n}
              onClick={() => setPage(n)}
              active={n === page}
              disabled={false}
            />
          ))}
          <PageBtn
            label="›"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          />
        </div>
      </div>

      {/* Modal de edición / alta */}
      {modalOpen && (
        <Modal
          open={modalOpen}
          mode={modalMode}
          data={editData}
          fkValue={fkValue}
          onClose={handleCloseModal}
          onSubmit={handleSubmitFromModal}
          inline={false}
        />
      )}

      {/* Confirmación de borrado */}
      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onConfirmDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
